import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatRelative } from "../lib/api";
import Layout from "../components/Layout";
import { useAuth } from "../contexts/AuthContext";
import { AtSign, Check, X, TrendingUp, VolumeX, Volume2 } from "lucide-react";

const TABS = [
  { key: "pending", label: "Venter på svar" },
  { key: "accepted", label: "Godtatt" },
  { key: "rejected", label: "Avslått" },
];

function CreatorInsights() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = () => api.get("/mentions/insights?days=30").then((r) => setData(r.data)).catch(() => setData(null));
  useEffect(() => { load(); }, []);

  const toggleMute = async (userId, currentlyMuted) => {
    setBusy(userId);
    try {
      await api.post(currentlyMuted ? "/mentions/unmute" : "/mentions/mute", { user_id: userId });
      await load();
    } catch { /* */ }
    finally { setBusy(null); }
  };

  if (!data) return null;

  return (
    <section className="border border-white/10 p-6 mb-10" data-testid="mention-insights">
      <div className="flex items-center gap-2 mb-1">
        <TrendingUp className="h-3.5 w-3.5 text-[var(--magenta)]" />
        <div className="font-meta text-[var(--magenta)]">SKAPER-INNSIKT · SISTE 30 DAGER</div>
      </div>
      <h2 className="font-fraunces italic text-2xl text-[var(--cream)] mb-5">Hvem nevner deg.</h2>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6 pb-6 border-b border-white/10">
        <Stat label="Totalt nevnt" value={data.total} accent />
        <Stat label="Godtatt" value={data.accepted} />
        <Stat label="Venter" value={data.pending} />
        <Stat label="Unike folk" value={data.unique_mentioners} />
      </div>

      {data.top_mentioners.length === 0 ? (
        <p className="font-fraunces italic text-[var(--cream-soft)]">
          Ingen mentions denne perioden.
        </p>
      ) : (
        <>
          <div className="font-meta mb-3">TOPP 5 SOM NEVNER DEG</div>
          <ul className="divide-y divide-white/5">
            {data.top_mentioners.map((row) => (
              <li key={row.user_id} className="py-3 flex items-center gap-3" data-testid={`top-mentioner-${row.user_id}`}>
                <div className="w-8 h-8 rounded-full bg-[var(--noir-elev)] overflow-hidden flex items-center justify-center shrink-0">
                  {row.user.avatar_url ? (
                    <img src={row.user.avatar_url} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <AtSign className="h-4 w-4 text-[var(--cream-faint)]" />
                  )}
                </div>
                <Link to={`/profile/${row.user.username}`}
                      className="font-fraunces italic text-base text-[var(--cream)] hover:text-[var(--magenta)] truncate flex-1 min-w-0">
                  @{row.user.username}
                </Link>
                <div className="font-meta text-[10px] text-[var(--cream-soft)] hidden sm:block">
                  {row.accepted} av {row.count} godtatt
                </div>
                <div className="font-meta text-[var(--magenta)] tabular-nums px-2">
                  {row.count}×
                </div>
                <button
                  onClick={() => toggleMute(row.user_id, row.is_muted)}
                  disabled={busy === row.user_id}
                  data-testid={`mute-${row.user_id}`}
                  className={`px-3 py-1.5 font-meta text-[10px] inline-flex items-center gap-1.5 transition-colors ${
                    row.is_muted
                      ? "bg-[var(--magenta)]/15 text-[var(--magenta)] border border-[var(--magenta)]/40"
                      : "border border-white/10 text-[var(--cream-soft)] hover:border-white/30"
                  }`}
                  title={row.is_muted ? "Opphev demping" : "Demp denne brukeren"}
                >
                  {row.is_muted ? <><Volume2 className="h-3 w-3" /> Dempet</> : <><VolumeX className="h-3 w-3" /> Demp</>}
                </button>
              </li>
            ))}
          </ul>
          {data.muted_count > 0 && (
            <p className="font-meta text-[10px] text-[var(--cream-faint)] mt-4">
              Du har dempet <strong className="text-[var(--magenta)]">{data.muted_count}</strong> bruker{data.muted_count !== 1 ? "e" : ""}. Mentions fra disse auto-avslås.
            </p>
          )}
        </>
      )}
    </section>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div>
      <div className={`font-fraunces italic text-3xl tabular-nums ${accent ? "text-[var(--magenta)]" : "text-[var(--cream)]"}`}>
        {value ?? 0}
      </div>
      <div className="font-meta text-[10px] text-[var(--cream-soft)] mt-1">{label}</div>
    </div>
  );
}

export default function Mentions() {
  const { user } = useAuth();
  const [tab, setTab] = useState("pending");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actingId, setActingId] = useState(null);

  const load = (which = tab) => {
    setLoading(true);
    return api
      .get(`/mentions?status=${which}&limit=50`)
      .then((r) => setItems(r.data))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!user || user === false) return;
    load(tab);
    // eslint-disable-next-line
  }, [user, tab]);

  const decide = async (id, decision) => {
    setActingId(id);
    try {
      await api.post(`/mentions/${id}/${decision}`);
      setItems((prev) => prev.filter((m) => m.id !== id));
    } finally {
      setActingId(null);
    }
  };

  if (user === false || user === null) {
    return (
      <Layout>
        <div className="font-fraunces italic text-[var(--cream-soft)]">
          <Link to="/login" className="text-[var(--magenta)]">Logg inn</Link> for å se omtaler.
        </div>
      </Layout>
    );
  }

  return (
    <Layout withSidebar={false}>
      {user?.is_creator && <CreatorInsights />}
      <div className="flex items-end justify-between mb-10 gap-4 flex-wrap">
        <div>
          <div className="font-meta mb-3">Samtykke</div>
          <h1 className="font-fraunces italic text-5xl text-[var(--cream)] leading-tight">
            Omtaler.
          </h1>
          <p className="font-manrope text-[var(--cream-soft)] mt-3 max-w-xl">
            Andre kan ikke nevne deg uten ditt samtykke. Godta omtalen for å la lenken
            til profilen din vises i innlegget.
          </p>
        </div>
        <Link to="/varsler" className="btn-ghost text-xs" data-testid="back-to-varsler">
          Tilbake til varsler
        </Link>
      </div>

      <div className="flex gap-1 border-b border-white/10 mb-8" data-testid="mention-tabs">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-3 font-meta text-xs transition-colors border-b-2 -mb-px ${
              tab === t.key
                ? "border-[var(--magenta)] text-[var(--cream)]"
                : "border-transparent text-[var(--cream-soft)] hover:text-[var(--cream)]"
            }`}
            data-testid={`mention-tab-${t.key}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="font-meta text-[var(--cream-soft)]">Laster…</div>
      ) : items.length === 0 ? (
        <div className="text-center py-20">
          <AtSign className="h-10 w-10 text-[var(--cream-faint)] mx-auto mb-3" />
          <div className="font-fraunces italic text-2xl text-[var(--cream-soft)]">
            {tab === "pending" ? "Ingen ventende omtaler." : "Tomt her."}
          </div>
        </div>
      ) : (
        <ul className="divide-y divide-white/5 max-w-3xl" data-testid="mention-list">
          {items.map((m) => (
            <li
              key={m.id}
              className="py-6 flex items-start gap-4"
              data-testid={`mention-${m.id}`}
            >
              <div className="h-9 w-9 rounded-full flex items-center justify-center shrink-0 bg-[var(--magenta)]/15 text-[var(--magenta)]">
                <AtSign className="h-4 w-4" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-fraunces text-[var(--cream)] text-base leading-snug">
                  <Link
                    to={`/profile/${m.actor_username}`}
                    className="text-[var(--magenta)] hover:underline"
                  >
                    @{m.actor_username}
                  </Link>{" "}
                  vil nevne deg i tråden{" "}
                  <Link
                    to={`/thread/${m.thread_id}`}
                    className="italic text-[var(--cream)] hover:text-[var(--magenta)]"
                  >
                    «{m.thread_title || "uten tittel"}»
                  </Link>
                </div>
                {m.preview && (
                  <blockquote className="mt-2 border-l border-white/10 pl-3 font-manrope text-sm text-[var(--cream-soft)]">
                    {m.preview}
                  </blockquote>
                )}
                <div className="font-meta mt-2">
                  {formatRelative(m.created_at)}
                  {m.status !== "pending" && (
                    <span className="ml-2 text-[var(--cream-faint)]">
                      · {m.status === "accepted" ? "Godtatt" : "Avslått"}
                      {m.decided_at ? ` ${formatRelative(m.decided_at)}` : ""}
                    </span>
                  )}
                </div>
              </div>
              {m.status === "pending" && (
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={() => decide(m.id, "accept")}
                    disabled={actingId === m.id}
                    className="btn-magenta text-xs inline-flex items-center gap-1.5"
                    data-testid={`mention-accept-${m.id}`}
                  >
                    <Check className="h-3 w-3" /> Godta
                  </button>
                  <button
                    onClick={() => decide(m.id, "reject")}
                    disabled={actingId === m.id}
                    className="btn-ghost text-xs inline-flex items-center gap-1.5"
                    data-testid={`mention-reject-${m.id}`}
                  >
                    <X className="h-3 w-3" /> Avslå
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </Layout>
  );
}
